import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ApplicationState } from '../../store';
import { ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import * as CustomerStateController from '../../store/StateControllers/Customers/CustomersStateController';
import { ToxnotReactComponentPaths } from '../core/ToxnotReactComponentPaths';
import { TableColumn, TableRow, ToxnotReactTable } from '../core/ToxnotReactTable';
import { ScrollableContent, TopActionsAndScrollableContentContainer, TopActionButtons, Header } from '../core/TopActionsAndScrollableContentContainer';
import { Customer } from '../../store/Models/Customers/Customer';
import { MyCustomersProps } from './MyCustomersCommon';
import FiltersContainer from '../core/FiltersContainer';
import { Filter, FilterId } from '../../store/Models/Filter';
import SelectFileForUpload from '../core/SelectFileForUpload';
import * as Utilities from '../../utilities/Utilities';
import { ActiveTabType } from './MyCustomerDetail';
import { NavPillsDetail } from '../core/NavPillsDetail';
import { SearchBar } from '../core/SearchBar';
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { SharedMaterialsFullCompany } from '../../store/Models/SharedMaterials/DataModel';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../core/ToxnotReactModal';

type MyCustomerHomeProps =
    MyCustomersProps &
    RouteComponentProps;

class MyCustomersHome extends ToxnotSignInRequiredComponent<MyCustomerHomeProps>{

    // ===============================================================================

    private _filterIdToFriendlyNameMap = { 
        [FilterId.MyCustomerSearch.toString()]: this.localizeString("Search"),
        [FilterId.HasActiveRequests.toString()]: this.localizeString("Has Active Requests")
    }

    private _filterValueToFriendlyNameMap: Record<string, string> = {
        ["yes"]: this.localizeString("Yes"),
        ["no"]: this.localizeString("No")
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    protected renderConcreteComponent() {
        return (
            <TopActionsAndScrollableContentContainer>
                <Header>
                    {this.renderHeader()}
                </Header>
                <TopActionButtons>
                    {this.renderTopActionButtons()}
                </TopActionButtons>
                <ScrollableContent>
                    {this.renderBody()}
                </ScrollableContent>
            </TopActionsAndScrollableContentContainer>
        );
    }

    // ===============================================================================

    private getRequestUrl(company: SharedMaterialsFullCompany) {
        if (company.customUrlName === null) {
            throw new Error("Only call this method if custom url name is not null");
        }

        return Utilities.addhostnameToString(ToxnotReactComponentPaths.RequestReportsBasePath + "/" + encodeURIComponent(company.customUrlName));
    }

    // ===============================================================================

    private getSharedMaterialCompaniesWithValidCustomUrlName() {
        return this.props.sharedMaterialCompanies.filter(c => (c.customUrlName?.trim() ?? "") !== "");
    }

    // ===============================================================================

    private renderRequestUrls() {
        const sharedMaterialsWithValidCustomUrlNames = this.getSharedMaterialCompaniesWithValidCustomUrlName();

        if (sharedMaterialsWithValidCustomUrlNames.length === 1) {
            return (
                <div id="request-url-container">
                    {this.renderRequestUrlRowContainer(sharedMaterialsWithValidCustomUrlNames[0], 0)}
                </div>
            );
        }

        return (
            <a role="button" onClick={e => $('#all-customer-request-urls-modal').modal('show')}>{this.localizeString("View all URLs")}</a>
        ); 
    }

    // ===============================================================================

    private renderRequestUrlRowContainer(company: SharedMaterialsFullCompany, key: string | number) {
        return (
            <div key={key} className="request-url-row-container">
                <div className="request-url">
                    <a href={this.getRequestUrl(company)} target="_blank">{this.getRequestUrl(company)}</a>
                </div>
                <div className="copy-request-url-container">
                    <button type="button" className="btn btn-sm btn-default" onClick={e => this.copyRequestUrlButton_click(company)}>{this.localizeString("Copy")}</button>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderHeader() {
        return (
            <React.Fragment>
                <div id="action-buttons" className="tn-section-padding-container">
                    <button id="add-customer-button" className="btn btn-default" onClick={e => this.addNewCustomerButton_click()}><span className="glyphicon glyphicon-plus"></span> {this.localizeString("Customer")}</button>
                    {" "}
                    <SelectFileForUpload acceptedUploadFileTypes={[".xlsx", ".csv"]} localizedUploadButtonText={this.localizeString("Import Customers")} fileSelected={this.selectImportCustomersFile_fileSelected} />
                    {" "}
                    <button id="delete-customers-button" className="btn btn-toxnot-danger" onClick={e => this.deleteCustomers_click()}><span className="glyphicon glyphicon-remove"></span></button>
                    {" "}
                    <a href="/files/Import%20Customers%20Template.xlsx">{this.localizeString("Download Import Template")}</a>
                    {" "}
                </div>
                {this.getUserContextForSignedInUser().userCapabilities.myCustomersEnabled && <div id="customer-request-url-container" className="tn-section-padding-container">
                    {this.hasRequestUrl()
                        ? (
                            <div className="customer-urls-intro-container">
                                <div>
                                    {this.localizeString("Share these URL's with customers so they can submit requests:")}
                                </div>
                                <div>
                                    {this.renderRequestUrls()}
                                </div>
                            </div>
                        )
                        : (
                            <div className="alert alert-warning">
                                {this.props.appServices.localizeString("Looks like you have not created a custom request url for any of your companies.")} <a role="button" onClick={e => this.props.history.push(ToxnotReactComponentPaths.CompanyPagesManager)}>{this.props.appServices.localizeString("Click here to start.")}</a>
                            </div>
                        )
                    }
                </div>}
                <ToxnotReactModal modalId={"all-customer-request-urls-modal"} localizedTitle={this.localizeString("Customer Request URLs")}>
                    <ToxnotReactModalBody>
                        {this.getSharedMaterialCompaniesWithValidCustomUrlName()
                            .map((c, i) => (
                                this.renderRequestUrlRowContainer(c, i)
                            ))}
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter localizer={this.props.appServices} enabledCloseButton={false}>
                        <button type="button" className="btn btn-toxnot-primary" data-dismiss="modal">{this.localizeString("Done")}</button>
                    </ToxnotReactModalFooter>
                </ToxnotReactModal>
            </React.Fragment>
        );      
    }

    // ===============================================================================

    private renderTopActionButtons() {
        return (
            <div id="search-and-filter-container" className="tn-section-padding-container">
                <div id="search-and-filter-controls">
                    <NavPillsDetail localizedNavTitles={[this.localizeString("All Customers"), this.localizeString("Outstanding Requests")]} options={{ includeDetail: false, borderBottom: false, marginBottom: false }} onNavIndexChanged={this.navPills_navIndexChanged} />
                    <div id="search-input-group-container">
                        <SearchBar localizedPlaceholder={this.localizeString("By Company or Contact Name/Email")} onSearch={this.addSearchFilter} clearSearchAfterSubmit={true}/>
                    </div>
                </div>
                <FiltersContainer
                    filters={this.props.activeCustomerFilters}
                    getFriendlyNameForFilter={this.getCustomerFilterDisplayString}
                    skipFilterIds={[FilterId.HasActiveRequests]}
                    removeFilter_click={this.removeFilter_click} />
            </div>
        );
    }

    // ===============================================================================

    private renderBody() {
        return (
            <div id="my-customer-home-table" className="tn-section-padding-container">
                <ToxnotReactTable
                    allRowModels={this.props.filteredCustomers}
                    localizer={this.props.appServices}
                    getRowForTable={this.getRow}
                    headerRow={this.getHeaderRow()}
                    rowClickEventHandler={this.customerRow_click}
                    enablePaging={true}
                    selectingRowOptions={{
                        onRowSelected: this.props.updateSelectedCustomerIds,
                        rowModelGetId: this.getCustomerIdFromCustomer
                    }}
                    fullSectionWidth={true}
                    defaultSortColumn={0}
                />
            </div>
        );
    }

    // ===============================================================================

    private getHeaderRow = () => {
        return [
            {
                value: this.localizeString("Company"),
                isSortable: true,
                getSortableValue: (c: Customer) => { return c.companyName ?? "" }
            },
            {
                value: this.localizeString("Contact")
            },
            {
                value: this.localizeString("Needs Response?")
            },
        ] as TableColumn<Customer>[];

    }

    // ===============================================================================

    private getRow = (customer: Customer, index: number): TableRow => {
        const renderedContactNames = (
            <React.Fragment>
                {customer.customerContacts.length === 0
                    ? <span>--</span>
                    : customer.customerContacts.take(2).map((contact, index) => <React.Fragment key={index}>{contact.name} &lt;{contact.email}&gt;<br /></React.Fragment>)
                }
                {(customer.customerContacts.length > 2) && <span>...</span>}
            </React.Fragment>
        );

        return {
            cols: [
                customer.companyName ?? this.localizeString("Unknown Company"),
                renderedContactNames,
                this.props.customerIdsToRequests === null ? null : this.props.customerIdsToRequests[customer.customerId].some(r => r.dateResponded === null) ? this.localizeString("Needs Response") : ""
            ]
        } as TableRow;
    }

    // ===============================================================================

    private hasRequestUrl() {
        if (this.props.sharedMaterialCompanies.length === 0) {
            return false;
        }
        if (!this.props.sharedMaterialCompanies.some(c => (c.customUrlName !== null && c.customUrlName.length > 0))) {
            return false;
        }

        return true;
    }

    // ===============================================================================

    private getCustomerIdFromCustomer = (customer: Customer) => customer.customerId;

    // ===============================================================================

    private getCustomerFilterDisplayString = (filter: Filter) => {
        return this._filterIdToFriendlyNameMap[filter.filterId] + ": " + (this._filterValueToFriendlyNameMap[filter.value] ?? filter.value);
    }

    // ===============================================================================

    private addSearchFilter = (searchInput: string) => {
        const existingSearchFilter = this.props.activeCustomerFilters.find(v => v.filterId === FilterId.MyCustomerSearch) ?? null;

        if (existingSearchFilter) {
            this.props.removeCustomerFilter(existingSearchFilter);
        }   

        this.props.addFilter({ filterId: FilterId.MyCustomerSearch, value: searchInput, filterParameter: null });
    }

    // ===============================================================================

    private selectImportCustomersFile_fileSelected = async (file: File) => {
        await this.props.uploadCustomers(file);
        window.location.reload();
    }

    // ===============================================================================

    private navigateToCustomerDetail = (customerId: string) => {
        localStorage.setItem("customer-last-active-tab", ActiveTabType.Info);
        this.props.history.push(`${ToxnotReactComponentPaths.MyCustomerDetailBasePath}/${customerId}`);
    }

    // ===============================================================================
    //
    // UI Events
    //

    private customerRow_click = (e: React.MouseEvent, model: Customer) => {
        this.props.history.push(`${ToxnotReactComponentPaths.MyCustomerDetailBasePath}/${model.customerId}`);
    }

    // ===============================================================================

    private addNewCustomerButton_click = async () => {
        this.props.createNewCustomer(this.navigateToCustomerDetail);
    }

    // ===============================================================================

    private deleteCustomers_click = async () => {
        if (Object.keys(this.props.selectedCustomerIds).length === 0) {
            alert(this.localizeString("Please select at least one customer to delete."));
            return;
        }

        await this.props.deleteSelectedCustomer();
    }

    // ===============================================================================

    private navPills_navIndexChanged = (navIndex: number) => {
        if (navIndex === 0) {
            // show all customers - clear any filters
            this.props.clearActiveRequestsFilters();
        }
        else {
            // just outstanding requests
            this.props.addFilter({ filterId: FilterId.HasActiveRequests, value: "yes", filterParameter: null });
        }
    }

    // ===============================================================================

    private removeFilter_click = (filter: Filter) => {
        this.props.removeCustomerFilter(filter);
    }

    // ===============================================================================

    private copyRequestUrlButton_click = (company: SharedMaterialsFullCompany) => {
        navigator.clipboard.writeText(this.getRequestUrl(company));
    }
}

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices, ...state.myCustomersState }),
    CustomerStateController.actionCreators
)(MyCustomersHome as any);
